<template>
<div class="container-fluid">
    <div class="row">
        
        <div class="col-12">
            <div class="d-flex align-items-center">
                <h1 class="flex-grow-1" data-test="pageTitle">
                    Specialty Mappings
                    <span class="d-none d-md-inline text-transform-none font-weight-normal" style="letter-spacing: 0;">(Used to set specialties on physician records during import)</span>
                </h1>    

                <div v-if="perms.can_administer_specialties" class="drop-down drop-down-show text-transform-none">
                    <span 
                        role="button" id="dropdownMenuLink" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"
                        class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none"
                        v-tooltip.bottom="'Options'"
                        data-test="specsContextMenu">
                        <i class="far fa-cog text-xl"></i>
                    </span>

                    <div class="dropdown-menu py-0 mr-4" aria-labelledby="dropdownMenuLink">
                        <router-link exact :to="{ name: 'admin.specialties' }" class="dropdown-item">Manage Specialties</router-link>
                        <router-link exact :to="{ name: 'admin.specialties.mappings' }" class="dropdown-item">Manage Mappings</router-link>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-6"  :class="{ 'pending' : pending }">
            <div class="card">
                <div class="card-header">
                    Mappings to Ember Specialties
                </div>
                <div class="card-body">

                    <div v-if="status != 'unchanged'" class="alert alert-warning bg-yellow-200 d-flex align-items-center justify-content-between">
                        <span v-if="status == 'changed'">Mappings have been changed but not saved.</span>
                        <span v-if="status == 'new'">Mappings have been created but not saved.</span>
                        <span class="btn btn-sm btn-primary text-nowrap" @click="saveMappedSpecialties()">Save Map</span>
                    </div>

                    <div v-if="!mappedSpecialties" class="text-gray-700 text-center">
                        <p>No existing mappings were found. Click <span @click="buildDefaultMap()">build map</span> to get started.</p>
                        <span class="btn btn-primary" @click="buildDefaultMap()">Build Map</span>
                    </div>

                    <template v-else>
                        <div class="border-bottom mb-3 pb-3 text-sm text-gray-700">
                            Ember specialties are in <b>bold</b> with mapped EMR items nested below. Click the <i class="fal fa-times-circle"></i> to remove a mapping. Add or remove specialties on the <router-link :to="{ name: 'admin.specialties' }">specialty admin page</router-link>.
                        </div>
                        <div v-for="specialty in mappedSpecialties" class="mb-2">
                            <div class="font-weight-bold">{{ specialty.label }}</div>
                            <div v-for="child in specialty.children" class="ml-2">
                                <i class="fal fa-long-arrow-right text-gray-500"></i> {{ child }}
                                <span 
                                    class="text-gray-500 text-sm cursor-pointer"
                                    @click="removeFromMap(specialty, child)">
                                    <i class="fal fa-times-circle"></i>
                                </span>
                            </div>
                        </div>

                        <div class="text-right px-35 pt-3 border-top" style="margin-left:-1.25rem; margin-right:-1.25rem;">
                            <span class="btn btn-primary" @click="saveMappedSpecialties()">Save Map</span>
                        </div>  
                    </template>
                </div>
            </div>
        </div>

        <div class="col-6">
            <div class="card">
                <div class="card-header">
                    EMR Specialties (with physician counts)
                </div>                            
                <div class="card-body">
                    <div class="border-bottom mb-2 pb-3 text-sm text-gray-700">
                        Click an EMR specialty to set its mapping. Items with a checkmark have a mapping. Specialties can have multiple mappings. Unmapped items will map to "other."
                    </div>
                    <emr-specialty-mapping-item
                        v-for="(emrSpec, index) in emrSpecialties.filter(item => item.label)"
                        :key="index"
                        :emrSpec="emrSpec"
                        @add-mapping="addToMap($event)"
                    />
                </div>
            </div>
        </div>

    </div>
</div>
</template>

<script>
    import { mapState } from 'vuex';
    import BreadcrumbsManager from '../../helpers/BreadcrumbManager';
    import EmrSpecialtyMappingItem from '../../components/EmrSpecialtyMappingItem'

	export default {
        mixins: [BreadcrumbsManager],

        components: {
            EmrSpecialtyMappingItem,
        },

        data() {
            return {
                mappedSpecialties: null,
                emrSpecialties: [],

                pending: false,
                status: 'unchanged',
            }
        },

        created() {
            // this.fetchEmrSpecialties();
            //this.fetchSpecialtyMap();
            //this.buildDefaultMap();
        },

        mounted() { 
        },

        activated() {
            this.fetchEmrSpecialties();
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
                { text: 'Settings', to: { name: '' } },
                { text: 'Specialties', to: { name: 'admin.specialties' } },
                { text: 'Mappings', to: { name: 'admin.specialties.mappings' } },
            ]);   
        },

        methods: {
            addToMap($event) {
                let mappedChildren = this.mappedSpecialties.find(item => item.label == $event.emberSpecLabel).children;

                if (!mappedChildren.includes($event.emrSpecLabel)) {
                    mappedChildren.push($event.emrSpecLabel);

                    this.emrSpecialties.find(item => item.label == $event.emrSpecLabel).checked = this.emrSpecialties.find(item => item.label == $event.emrSpecLabel).checked + 1; 
                    this.setStatus('changed');               
                }
            },

            buildDefaultMap() {
                this.mappedSpecialties = null;

                let map = this.options.specialties.map(emberSpec => (
                    {
                        label: emberSpec.label,
                        children: [],
                    })
                );

                this.emrSpecialties.forEach(emrSpec => {
                    if (map.some(item => item.label == emrSpec.label)) {
                        map.find(item => item.label == emrSpec.label).children.push(emrSpec.label);
                        emrSpec.checked = emrSpec.checked ? emrSpec.checked +1 : 1;
                    }
                });
                this.mappedSpecialties = map;
                this.setStatus('new');
            },

            fetchEmrSpecialties() {
                axios.get(`/api/specialties/counts`).then(({data}) => {
                    let specs = data.map(item => ({ ...item, checked: 0 }));
                    this.emrSpecialties = specs;

                    this.fetchSpecialtyMap();
                })
            },

            fetchSpecialtyMap() {
                axios.get('/api/specialties/map').then(({data}) => {
                    if (!data) {
                        this.setStatus('unchanged');
                    } else {
                        this.setStatus('unchanged');
                        this.mappedSpecialties = data.sort((a, b) => a.label.localeCompare(b.label));

                        this.emrSpecialties.forEach(emrSpec => {


                            let num = this.mappedSpecialties.reduce(function (n, spec) {
                                return n + (spec.children.includes(emrSpec.label));
                            }, 0);

                            console.log(emrSpec.label + '-' + num);
                            emrSpec.checked = num;
                        });
                    }
                });
            },

            removeFromMap(specialty, child) {
                let emberSpec = this.mappedSpecialties.find(item => item.label == specialty.label);
                let childIndex = emberSpec.children.findIndex(item => item == child);
                emberSpec.children.splice(childIndex, 1);
                
                this.emrSpecialties.find(spec => spec.label == child).checked = this.emrSpecialties.find(spec => spec.label == child).checked -1;
                this.setStatus('changed');
            },

            saveMappedSpecialties() {
                this.pending = true;
                axios.post('/api/specialties/map', this.mappedSpecialties).then(() => {
                    this.setStatus('unchanged');
                    this.pending = false;
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 4000,
                        icon: 'success',
                        title: 'Specialty Map saved',
                    }); 
                });
            },

            setStatus(value) {
                this.status = value;
            }

        },

        computed: {
            ...mapState(['options', 'perms']),

            // clientSpecialtyIds() {
            //     return this.options.specialties.map(spec => spec.id);
            // }
        },

        // watch: {
        //     '$store.state.options.specialties'() {
        //         this.clientSpecialties = this.options.specialties.map(spec => spec.id);
        //     }
        // }
    }
</script>

<style scoped>
    .spec-check { width:60px; }
    .actions { width:120px; }
</style>